/**
 * RateCardManager — Settings ▸ Rate Card. Resource Plan Epic 12 (RP-12.1, RP-12.2).
 *
 * RP-12.2: "Both recompute live as rates or mix % are edited, and are shown above the card
 * exactly as the source artefact presents them." That is why the derived header is
 * computed from the LOCAL DRAFT via `@/lib/rate-card` — the same pure module the server
 * uses — rather than by round-tripping to the API on every keystroke. The card the planner
 * sees and the card the server saves cannot disagree, because they run identical code.
 *
 * COST: the column renders only when the payload carries it. `avgInternalCost` is absent
 * from an [ACCT] response, not blank (non-negotiable 3), so `canViewCost` gates the column
 * rather than a CSS class. Blended Cost and the Card Margin are likewise header figures an
 * [ACCT] never receives — but the Card MARGIN is a percentage and IS shown, per §12.
 *
 * TERMINOLOGY (§3): the artefact's column header says "Role"; here it is a Labor Category,
 * with a tooltip so [FIN] recognises their own card. The tooltip translates, the entity
 * does not.
 */

import { Link } from "@tanstack/react-router";
import { AlertTriangle, Info, RotateCcw, Save } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type RateCardView,
  useRateCard,
  useSaveRateCard,
  useUpdateRateBasis,
} from "@/hooks/use-rate-card";
import {
  computeCardMetrics,
  FULL_PERCENT_BP,
  formatBpAsPercent,
  formatMicrosAsMoney,
} from "@/lib/rate-card";
import { cn } from "@/lib/utils";

/** The draft the grid edits. Rates are keyed `${categoryId} ${basisId}`. */
interface Draft {
  mix: Record<string, string>;
  cost: Record<string, string>;
  rates: Record<string, string>;
}

function draftFrom(card: RateCardView): Draft {
  const mix: Record<string, string> = {};
  const cost: Record<string, string> = {};
  const rates: Record<string, string> = {};
  for (const category of card.categories) {
    mix[category.id] = category.projectMixPercent;
    // Absent for anyone without viewPlanCost — the draft carries "" and the column is
    // never rendered, so nothing is ever saved back over a value we were not sent.
    cost[category.id] = category.avgInternalCost ?? "";
    for (const [basisId, rate] of Object.entries(category.rates)) {
      rates[`${category.id} ${basisId}`] = rate ?? "";
    }
  }
  return { mix, cost, rates };
}

/** A decimal(x,2) or empty. Anything else is left for the server to reject. */
const DECIMAL2 = /^\d+(\.\d{1,2})?$/;

function isEditableNumber(value: string): boolean {
  return value === "" || DECIMAL2.test(value);
}

export function RateCardManager() {
  const { data: card, isLoading, isError, error, refetch } = useRateCard();
  const save = useSaveRateCard();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));

  // The draft is seeded from the server card on first render and after each save; a null
  // draft means "showing exactly what the server has".
  const working = draft ?? (card ? draftFrom(card) : null);

  /**
   * The live recompute (RP-12.2). Runs the SAME pure module the server runs, on the
   * draft — so the header cannot show one blended rate while the save produces another.
   */
  const metrics = useMemo(() => {
    if (!card || !working) return null;
    try {
      return computeCardMetrics({
        categories: card.categories.map((c) => ({
          id: c.id,
          name: c.name,
          active: c.active,
          projectMixPercent: working.mix[c.id] || "0.00",
          // Cost is only in the draft when we were sent it. Without it the blended COST
          // is meaningless — the header hides it, and Card Margin with it.
          avgInternalCost: working.cost[c.id] || "0.00",
        })),
        entries: card.categories.flatMap((c) =>
          card.bases
            .map((b) => ({
              laborCategoryId: c.id,
              rateBasisId: b.id,
              billRate: working.rates[`${c.id} ${b.id}`] ?? "",
            }))
            .filter((e) => e.billRate !== ""),
        ),
        bases: card.bases,
      });
    } catch {
      // A half-typed number ("12.") is not a bug — the header simply waits for a value it
      // can parse rather than throwing the page away.
      return null;
    }
  }, [card, working]);

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load the Rate Card"} onRetry={refetch} />
    );
  }
  if (isLoading || !card || !working) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const activeCategories = card.categories.filter((c) => c.active);
  const mixBalanced = metrics?.mixTotalBp === FULL_PERCENT_BP;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(draftFrom(card));

  const setCell = (group: keyof Draft, key: string, value: string) => {
    if (!isEditableNumber(value)) return;
    setDraft({ ...working, [group]: { ...working[group], [key]: value } });
  };

  const onSave = () => {
    save.mutate(
      {
        categories: card.categories.map((c) => ({
          id: c.id,
          projectMixPercent: working.mix[c.id] || "0.00",
          avgInternalCost: working.cost[c.id] || c.avgInternalCost || "0.00",
        })),
        rates: card.categories.flatMap((c) =>
          card.bases.map((b) => {
            const value = working.rates[`${c.id} ${b.id}`] ?? "";
            return {
              laborCategoryId: c.id,
              rateBasisId: b.id,
              // "" means no rate on this basis — which is how Essentials stays incomplete
              // rather than being filled with a meaningless zero.
              billRate: value === "" ? null : value,
            };
          }),
        ),
        effectiveFrom: new Date(`${effectiveFrom}T00:00:00.000Z`).toISOString(),
      },
      { onSuccess: () => setDraft(null) },
    );
  };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-display text-lg font-semibold">Rate Card</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          The governed rates and margin floor every Resource Plan is priced against.
        </p>
      </header>

      <DerivedHeader card={card} metrics={metrics} />

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-base">Nymbl Standard Rate Card</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Saving closes the current rates and opens new ones — history is never overwritten.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className="text-xs text-muted-foreground" htmlFor="effective-from">
              Effective from
            </label>
            <Input
              id="effective-from"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="w-40"
            />
            {dirty && (
              <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
                <RotateCcw className="h-4 w-4" />
                Discard
              </Button>
            )}
            <Button size="sm" onClick={onSave} disabled={!dirty || !mixBalanced || save.isPending}>
              <Save className="h-4 w-4" />
              {save.isPending ? "Saving…" : "Save card"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="sticky left-0 z-10 bg-muted/50 px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      Labor Category
                      <span
                        title="The rate card artefact calls this “Role” — here, the priced category is a Labor Category."
                        className="cursor-help"
                      >
                        <Info className="h-3 w-3" />
                      </span>
                    </span>
                  </th>
                  {card.canViewCost && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                      Avg Internal Cost
                    </th>
                  )}
                  {card.bases.map((basis) => (
                    <th
                      key={basis.id}
                      className="px-4 py-3 text-right text-xs font-medium text-muted-foreground"
                    >
                      {basis.name}
                      {!basis.complete && (
                        <AlertTriangle className="ml-1 inline h-3 w-3 text-warning" />
                      )}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                    Mix %
                  </th>
                </tr>
              </thead>
              <tbody>
                {card.categories.map((category) => (
                  <tr
                    key={category.id}
                    className={cn(
                      "border-b last:border-0 hover:bg-muted/30 transition-colors",
                      !category.active && "opacity-50",
                    )}
                  >
                    <td className="sticky left-0 z-10 bg-background px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{category.name}</span>
                        {category.podEligible ? (
                          <Badge variant="secondary" className="text-xs">
                            {category.disciplineCode}
                          </Badge>
                        ) : null}
                        {!category.active && (
                          <Badge variant="outline" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </td>
                    {card.canViewCost && (
                      <td className="px-4 py-2">
                        <NumericCell
                          value={working.cost[category.id] ?? ""}
                          onChange={(v) => setCell("cost", category.id, v)}
                          ariaLabel={`Avg Internal Cost for ${category.name}`}
                        />
                      </td>
                    )}
                    {card.bases.map((basis) => (
                      <td key={basis.id} className="px-4 py-2">
                        <NumericCell
                          value={working.rates[`${category.id} ${basis.id}`] ?? ""}
                          onChange={(v) => setCell("rates", `${category.id} ${basis.id}`, v)}
                          ariaLabel={`${basis.name} rate for ${category.name}`}
                          placeholder="—"
                        />
                      </td>
                    ))}
                    <td className="px-4 py-2">
                      <NumericCell
                        value={working.mix[category.id] ?? ""}
                        onChange={(v) => setCell("mix", category.id, v)}
                        ariaLabel={`Avg Project Mix % for ${category.name}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/30">
                  <td className="sticky left-0 z-10 bg-muted/30 px-4 py-3 text-xs font-medium">
                    {activeCategories.length} active
                  </td>
                  {card.canViewCost && (
                    <td className="px-4 py-3 text-right text-xs tabular-nums text-muted-foreground">
                      {metrics ? `$${formatMicrosAsMoney(metrics.blendedCostMicros)}` : "—"}
                    </td>
                  )}
                  {card.bases.map((basis) => {
                    const m = metrics?.perBasis.find((b) => b.basisId === basis.id);
                    return (
                      <td
                        key={basis.id}
                        className="px-4 py-3 text-right text-xs font-medium tabular-nums"
                      >
                        {m?.blendedRateMicros != null
                          ? `$${formatMicrosAsMoney(m.blendedRateMicros)}`
                          : "—"}
                      </td>
                    );
                  })}
                  <td
                    className={cn(
                      "px-4 py-3 text-right text-xs font-semibold tabular-nums",
                      mixBalanced ? "text-success" : "text-destructive",
                    )}
                  >
                    {metrics ? `${formatBpAsPercent(metrics.mixTotalBp)}%` : "—"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {!mixBalanced && (
            <p className="mt-3 text-xs text-destructive">
              Avg Project Mix % must total exactly 100.00% before the card can be saved.
            </p>
          )}
        </CardContent>
      </Card>

      <TierBasisPointer />
      <MarginPolicy card={card} />
    </div>
  );
}

/** RP-12.2: Blended Rate and Card Margin per basis, live from the draft. */
function DerivedHeader({
  card,
  metrics,
}: {
  card: RateCardView;
  metrics: ReturnType<typeof computeCardMetrics> | null;
}) {
  const incomplete = card.bases.filter((b) => !b.complete);
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {card.bases.map((basis) => {
          const m = metrics?.perBasis.find((b) => b.basisId === basis.id);
          const blended = m?.blendedRateMicros ?? null;
          const margin = m?.cardMarginBp ?? null;
          return (
            <Card key={basis.id} className={cn(!basis.complete && "border-warning/50")}>
              <CardContent className="py-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {basis.name}
                  </span>
                  <FloorBadge basis={basis} />
                </div>
                <p className="mt-2 font-display text-2xl font-bold tabular-nums">
                  {blended === null ? "—" : `$${formatMicrosAsMoney(blended)}`}
                </p>
                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                  {/* Card Margin is a percentage, so [ACCT] sees it (§12) — but it needs
                      cost to derive, so it is null when cost was not sent. */}
                  {margin === null
                    ? card.canViewCost
                      ? "Card Margin unavailable"
                      : "Card Margin needs cost access"
                    : `${formatBpAsPercent(margin)}% Card Margin`}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {basis.mappedTierNames.length > 0
                    ? `${basis.mappedTierNames.join(", ")} tier${basis.mappedTierNames.length > 1 ? "s" : ""}`
                    : "No tier mapped"}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {card.canViewCost && metrics && (
        <p className="text-xs text-muted-foreground">
          Blended Cost{" "}
          <span className="font-medium tabular-nums text-foreground">
            ${formatMicrosAsMoney(metrics.blendedCostMicros)}
          </span>{" "}
          — one Avg Internal Cost per Labor Category, shared across every Rate Basis.
        </p>
      )}

      {incomplete.map((basis) => (
        <div
          key={basis.id}
          className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-50 px-4 py-3 text-sm text-warning-950 dark:bg-warning-950/20 dark:text-warning-100"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{basis.incompleteMessage}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * RP-7.1: green at or above floor + band · amber at or above floor but within the band ·
 * red below floor. Amber is a warning ABOVE the floor, not a shade of failure below it.
 */
function FloorBadge({ basis }: { basis: RateCardView["bases"][number] }) {
  if (basis.cardMarginStatus === null) return null;
  const label =
    basis.cardMarginStatus === "green"
      ? "Above floor"
      : basis.cardMarginStatus === "amber"
        ? "Near floor"
        : "Below floor";
  return (
    <span
      title={`Floor ${basis.marginFloorPercent}% (${basis.marginFloorSource === "basis" ? "override" : "firm-wide"}), amber band ${basis.marginAmberBandPoints} points`}
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-medium",
        basis.cardMarginStatus === "green" && "bg-success-100 text-success-700",
        basis.cardMarginStatus === "amber" && "bg-warning-100 text-warning-700",
        basis.cardMarginStatus === "red" && "bg-error-100 text-error-700",
      )}
    >
      {label}
    </span>
  );
}

/**
 * RP-12.3's Tier → Rate Basis map used to be an inline <select> here, and it did not obey
 * this card's own draft/Save/Discard model: changing it fired an immediate, un-undoable
 * PUT while the card stayed clean and Save stayed disabled. Two persistence models on one
 * screen. The map now lives on Settings ▸ Tiers behind an explicit Save, next to the rest
 * of the tier's configuration.
 */
function TierBasisPointer() {
  return (
    <section className="rounded-lg border bg-muted/30 px-5 py-4">
      <h3 className="font-display text-base font-semibold">Which tiers price on which card</h3>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Each Engagement Standard Tier is mapped to exactly one Rate Basis on{" "}
        <Link to="/dashboard/settings/tiers" className="font-medium text-primary underline">
          Settings → Tiers
        </Link>
        . There is no fallback: a tier mapped to a Rate Basis with no rates blocks plan creation.
      </p>
    </section>
  );
}

/** RP-D3: a global floor with a nullable per-basis override. NULL inherits. */
function MarginPolicy({ card }: { card: RateCardView }) {
  const update = useUpdateRateBasis();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Margin floor by Rate Basis</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Absolute percentages, not offsets from Card Margin. Blank inherits the firm-wide figure.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                  Rate Basis
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                  Floor %
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">
                  Amber band
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                  Source
                </th>
              </tr>
            </thead>
            <tbody>
              {card.bases.map((basis) => (
                <tr key={basis.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{basis.name}</td>
                  <td className="px-4 py-2">
                    <OverrideCell
                      value={basis.marginFloorSource === "basis" ? basis.marginFloorPercent : ""}
                      placeholder={card.globalMarginFloorPercent}
                      ariaLabel={`Margin floor override for ${basis.name}`}
                      disabled={update.isPending}
                      onCommit={(v) =>
                        update.mutate({ id: basis.id, marginFloorPercent: v === "" ? null : v })
                      }
                    />
                  </td>
                  <td className="px-4 py-2">
                    <OverrideCell
                      value={
                        basis.marginAmberBandSource === "basis" ? basis.marginAmberBandPoints : ""
                      }
                      placeholder={card.globalMarginAmberBandPoints}
                      ariaLabel={`Amber band override for ${basis.name}`}
                      disabled={update.isPending}
                      onCommit={(v) =>
                        update.mutate({ id: basis.id, marginAmberBandPoints: v === "" ? null : v })
                      }
                    />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {basis.marginFloorSource === "basis" || basis.marginAmberBandSource === "basis"
                      ? "Override"
                      : "Inherits firm-wide"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function NumericCell({
  value,
  onChange,
  ariaLabel,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
}) {
  return (
    <Input
      aria-label={ariaLabel}
      // `inputMode` rather than type="number": money crosses the wire as a STRING, and a
      // number input would hand back a float64 and lose the exactness the card depends on.
      inputMode="decimal"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-24 text-right tabular-nums"
    />
  );
}

/** An override field that commits on blur — a mutation per keystroke would be absurd. */
function OverrideCell({
  value,
  placeholder,
  ariaLabel,
  disabled,
  onCommit,
}: {
  value: string;
  placeholder: string;
  ariaLabel: string;
  disabled: boolean;
  onCommit: (value: string) => void;
}) {
  const [local, setLocal] = useState(value);
  return (
    <Input
      aria-label={ariaLabel}
      inputMode="decimal"
      value={local}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => isEditableNumber(e.target.value) && setLocal(e.target.value)}
      onBlur={() => local !== value && onCommit(local)}
      className="h-8 w-24 text-right tabular-nums"
    />
  );
}
